import { useApolloClient, useMutation } from '@base-app/web2'
import { memo, type ReactNode, useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  CREATE_COLLABORATOR_FUNCTION,
  UPDATE_COLLABORATOR_FUNCTION,
} from '../../../graphql/collaboratorFunctions/mutations'
import {
  COLLABORATOR_FUNCTION_QUERY,
  COLLABORATOR_FUNCTIONS_QUERY,
} from '../../../graphql/collaboratorFunctions/queries'
import type { CollaboratorFunction } from '../../../graphql/collaboratorFunctions/types'
import { useRecordById } from '../../../hooks/useRecordById'
import { useToast } from '../../../hooks/useToast'
import { refetchQuietly } from '../../../lib/apolloRefetch'
import { dropSearchedLists, insertAfterLockedRows } from '../../../lib/cacheLists'
import {
  collaboratorFunctionErrorMessage,
  collaboratorFunctionErrorReason,
} from '../../../lib/collaboratorFunctionErrors'
import { functionNameLength, normalizeFunctionName } from '../../../lib/functionName'
import { Button, FormActions } from '../../atoms'
import { FormField, FormLoadState } from '../../molecules'

// Same bound the API enforces on a function name
const MAX_NAME_LENGTH = 60

interface CollaboratorFunctionFormFieldsProps {
  collaboratorFunction?: CollaboratorFunction
  clientId: string
  appId: string
  onSuccess: () => void
  onClose: () => void
  // Renders every field as its pulsing placeholder while the record loads
  skeleton?: boolean
}

export const CollaboratorFunctionFormFields = memo(
  ({
    collaboratorFunction,
    clientId,
    appId,
    onSuccess,
    onClose,
    skeleton,
  }: CollaboratorFunctionFormFieldsProps) => {
    const { t } = useTranslation('admin-mobifull')
    const { showToast } = useToast()
    const client = useApolloClient()
    const isEdit = !!collaboratorFunction
    const [name, setName] = useState(collaboratorFunction?.name ?? '')
    const [errors, setErrors] = useState<{ name?: string }>({})

    const makeOptimistic = useCallback(
      (id: string): CollaboratorFunction => ({
        __typename: 'CollaboratorFunction',
        id,
        client_id: clientId,
        application_id: appId,
        key: collaboratorFunction?.key ?? '',
        name: normalizeFunctionName(name),
        locked: false,
        collaborators_count: collaboratorFunction?.collaborators_count ?? 0,
        created_at: collaboratorFunction?.created_at ?? new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }),
      [clientId, appId, collaboratorFunction, name],
    )

    const [createFunction, { loading: creating }] = useMutation(CREATE_COLLABORATOR_FUNCTION, {
      optimisticResponse: { createCollaboratorFunction: makeOptimistic(`temp-${Date.now()}`) },
      update(cache, result) {
        const item = result.data?.createCollaboratorFunction
        if (!item) {
          return
        }
        // The API lists locked functions first, so the new row goes below them (not on top)
        insertAfterLockedRows(cache, 'collaboratorFunctions', item, {
          clientId,
          applicationId: appId,
        })
      },
      onError(err) {
        showToast(collaboratorFunctionErrorMessage(err, t), 'error')
        // No permission: there is nothing to retry here
        if (collaboratorFunctionErrorReason(err) === 'FORBIDDEN') {
          onClose()
        }
      },
      onCompleted() {
        // One extra list request per create, on purpose: the exact position of the new row
        // (and whether it matches an open search) is the API's order, not ours
        dropSearchedLists(client.cache, 'collaboratorFunctions', { clientId, applicationId: appId })
        refetchQuietly(client.refetchQueries({ include: [COLLABORATOR_FUNCTIONS_QUERY] }))
        onSuccess()
      },
    })

    const [updateFunction, { loading: updating }] = useMutation(UPDATE_COLLABORATOR_FUNCTION, {
      optimisticResponse: {
        updateCollaboratorFunction: makeOptimistic(collaboratorFunction?.id ?? ''),
      },
      onError(err) {
        showToast(collaboratorFunctionErrorMessage(err, t), 'error')
        // The function is gone: leave its row and reload the list, as the message says
        const reason = collaboratorFunctionErrorReason(err)
        if (reason === 'FUNCTION_NOT_FOUND') {
          refetchQuietly(client.refetchQueries({ include: [COLLABORATOR_FUNCTIONS_QUERY] }))
          onClose()
        } else if (reason === 'FORBIDDEN') {
          onClose()
        }
      },
      onCompleted() {
        // A renamed row may no longer match an open search (or now match it): drop the searched
        // lists once the server answered so they refetch; unsearched lists update by id
        dropSearchedLists(client.cache, 'collaboratorFunctions', { clientId, applicationId: appId })
        // One extra list request per rename, on purpose (like create): a new name can change the
        // row's position, and the order is the API's, not ours
        refetchQuietly(client.refetchQueries({ include: [COLLABORATOR_FUNCTIONS_QUERY] }))
        onSuccess()
      },
    })

    const loading = creating || updating

    const validate = useCallback(() => {
      const errs: { name?: string } = {}
      const normalized = normalizeFunctionName(name)
      if (!normalized) {
        errs.name = t('common.form.nameRequired')
      } else if (functionNameLength(normalized) > MAX_NAME_LENGTH) {
        errs.name = t('collaboratorFunctions.errors.FUNCTION_NAME_TOO_LONG')
      }
      setErrors(errs)
      return Object.keys(errs).length === 0
    }, [name, t])

    const handleSubmit = useCallback(
      (e: React.FormEvent) => {
        e.preventDefault()
        if (!validate()) {
          return
        }
        const trimmed = normalizeFunctionName(name)
        if (isEdit) {
          updateFunction({ variables: { id: collaboratorFunction.id, name: trimmed } })
        } else {
          createFunction({
            variables: { client_id: clientId, application_id: appId, name: trimmed },
          })
        }
      },
      [
        validate,
        name,
        isEdit,
        collaboratorFunction?.id,
        clientId,
        appId,
        updateFunction,
        createFunction,
      ],
    )

    return (
      <form onSubmit={handleSubmit} noValidate aria-busy={skeleton}>
        <FormField
          skeleton={skeleton}
          id="function-name"
          label={t('collaboratorFunctions.form.nameLabel')}
          hint={t('collaboratorFunctions.form.nameHint')}
          autoComplete="off"
          name="name"
          value={name}
          onChange={e => setName(e.target.value)}
          error={errors.name}
          required
        />
        <FormActions>
          <Button type="button" variant="secondary" onClick={onClose} disabled={loading}>
            {t('common.actions.cancel')}
          </Button>
          <Button skeleton={skeleton} type="submit" disabled={loading}>
            {loading
              ? t('collaboratorFunctions.form.saving')
              : t('collaboratorFunctions.form.save')}
          </Button>
        </FormActions>
      </form>
    )
  },
)

CollaboratorFunctionFormFields.displayName = 'CollaboratorFunctionFormFields'

interface CollaboratorFunctionFormProps
  extends Omit<CollaboratorFunctionFormFieldsProps, 'collaboratorFunction'> {
  // The record to edit, by id; omit it to create a new one
  id?: string
}

// Loads the record by id (from the cache when the list has it, otherwise from the API)
export const CollaboratorFunctionForm = memo(({ id, ...props }: CollaboratorFunctionFormProps) => {
  const { t } = useTranslation('admin-mobifull')
  const { record, status } = useRecordById(
    COLLABORATOR_FUNCTION_QUERY,
    id,
    data => data.collaboratorFunction,
  )
  // Always mounted, and outside the child keyed by the record id (a key change remounts that
  // child): only its text changes, so a screen reader announces the change (K-4, F-U2)
  const statusRegion = (
    <p className="sr-only" role="status">
      {status === 'loading' ? t('common.loading') : ''}
    </p>
  )
  let content: ReactNode
  if (status === 'loading') {
    content = <CollaboratorFunctionFormFields skeleton {...props} />
  } else if (status !== 'new' && status !== 'ready') {
    content = <FormLoadState status={status} onClose={props.onClose} />
  } else {
    content = (
      <CollaboratorFunctionFormFields key={id ?? 'new'} collaboratorFunction={record} {...props} />
    )
  }
  return (
    <>
      {statusRegion}
      {content}
    </>
  )
})

CollaboratorFunctionForm.displayName = 'CollaboratorFunctionForm'
